import {
  useConfirmMfaSetup,
  type MfaSetupConfirmation,
} from '@/http/queries/useConfirmMfaSetup';
import { useSendMfaEmailCode } from '@/http/queries/useSendMfaEmailCode';
import { T } from '@/i18n/T';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import LoadingButton from '@components/Button/LoadingButton';
import { Button, Typography } from '@eye-able/ui';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import CodeInput from './CodeInput';
import MfaErrorAlert, { type MfaErrorOverrides } from './MfaErrorAlert';
import SendCodeButton from './SendCodeButton';

interface EmailCodeSetupProps {
  /** From the login's enrol step, or from the proof route in the profile. */
  challenge: string;
  email: string;
  onConfirmed: (result: MfaSetupConfirmation) => void;
  onBack: () => void;
  /** From the profile a dead challenge means the setup expired, not the login. */
  errorOverrides?: MfaErrorOverrides;
}

/** Mails the first code once on mount; later ones on request. */
function useMailedCode(challenge: string) {
  const send = useSendMfaEmailCode();
  const { mutate } = send;
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    mutate({ challenge });
  }, [mutate, challenge]);
  return send;
}

/** Proves the user reads their mail, which turns the mail method on. */
function EmailCodeSetup({
  challenge,
  email,
  onConfirmed,
  onBack,
  errorOverrides,
}: EmailCodeSetupProps) {
  const { t } = useInlineTranslation();
  const send = useMailedCode(challenge);
  const confirm = useConfirmMfaSetup();
  const [code, setCode] = useState('');
  const codeId = useId();
  const errorId = useId();
  const ready = code.length === 6;

  // Takes the code as an argument: on auto-submit the state is one render behind.
  const submit = (value: string) => {
    if (value.length < 6 || confirm.isPending) return;
    confirm.mutate(
      { challenge, type: 'email', code: value },
      { onSuccess: onConfirmed, onError: () => setCode('') },
    );
  };
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit(code);
  };

  return (
    <form onSubmit={onSubmit} className='eye:flex eye:flex-col eye:gap-16'>
      <Typography>
        <T k='mfaEmailCodeSentTo' options={{ email }} />
      </Typography>
      <div className='eye:flex eye:flex-col eye:gap-3xs'>
        <Typography tag='label' variant='body-m-highlight' htmlFor={codeId}>
          <T k='mfaEnterMailCode' />
        </Typography>
        <CodeInput
          id={codeId}
          value={code}
          onChange={(value) => {
            setCode(value);
            if (confirm.error) confirm.reset();
          }}
          onComplete={submit}
          describedBy={confirm.error ? errorId : undefined}
          invalid={!!confirm.error}
          disabled={confirm.isPending}
          autoFocus
        />
      </div>
      <SendCodeButton
        sent={!!send.data}
        resendAt={send.data?.resendAt ?? null}
        onSend={() => send.mutate({ challenge })}
        disabled={send.isPending}
      />
      <MfaErrorAlert
        id={errorId}
        error={confirm.error ?? send.error}
        overrides={errorOverrides}
      />
      <div className='eye:flex eye:justify-end eye:gap-2xs'>
        <Button
          type='button'
          variant='outline'
          onClick={onBack}
          disabled={confirm.isPending}
        >
          {t('back')}
        </Button>
        <LoadingButton
          type='submit'
          variant='yellow'
          loading={confirm.isPending}
          disabled={!ready}
        >
          {t('mfaActivate')}
        </LoadingButton>
      </div>
    </form>
  );
}

export default EmailCodeSetup;
